const cdn = '/assets/cdn';

export const brand = {
  name: 'Centerfire Services',
  tagline: 'Mobile mechanics and welding services in and around Bandera County, TX.',
  scheduleText: 'Schedule now',
  phone: '(210) 445-4277',
  copyright: '© Centerfire Services. All Rights Reserved 2026',
  logo: '/assets/local/centerfire-logo.png',
  icon: '/assets/local/centerfire-icon.png',
};

export const home = {
  hero: {
    title: 'On-Site Maintenance and Repairs',
    description:
      'Centerfire Services delivers proactive, on-site care for trucks and equipment of every kind. Our managed maintenance approach tracks service intervals, monitors equipment condition, and addresses developing issues early—giving owners the confidence that their assets are ready when called upon.',
    cta: 'Schedule now',
  },
  /* The paragraphs run long on purpose. The record-keeping argument is the one
     thing here a competitor cannot copy by buying a grease gun, so it is worth
     the room — and naming the marker-on-the-filter status quo does more work
     than any adjective would. Every capability described is shipped: the QR
     tag and PIN gate are the /maintunits access flow, and the operator hour
     entry is submitHourReading in maintainedUnits.js. Keep it that way. */
  whyUs: {
    title: 'The record is the product.',
    paragraphs: [
      'Industry standard is a magic marker. Somebody writes the hours on the filter and hopes the number survives long enough for anyone to notice the interval has passed. When the change finally happens it gets done by whoever is free that day, and nothing is written down—no fresh number on the filter, no note of what went in. If it is reported at all, it becomes one more receipt in a folder, filed alongside every repair invoice the machine has ever collected, waiting for the day it is sold and somebody has to prove it was looked after.',
      'We think the record is the part worth fixing, and that is where we have put our effort. Greasing a joint is not a hard problem; knowing what was done, when, and what it means for the next interval is. Every machine we take on gets a CFM number and one place where its maintenance and its repairs live together, in order, for as long as we have it.',
      'That record opens from a desk by CFM number, or at the machine itself by scanning the QR tag and entering the PIN. An operator standing beside it can push the current hour reading straight in, which keeps hours since a repair and hours until a specific service item accurate without anyone running a tally in their head. The work of remembering comes off the operator and off an already loaded foreman, and sits with the system instead.',
      'That baseline program is the real product. At the minimum subscription we are putting eyes on the machine, topping off fluids and recording what it took—steady fluid loss is often the first thing a developing failure tells you—checking for visible wear and leaks, and logging every repair and service item we are hired to do into a history the owner can open at any time. Monthly greasing, washing, a standing annual oil change: all worth having, but they are additions to that foundation, not the reason for it.',
    ],
    image: '/assets/local/centerfire-service-truck.webp',
  },
};

export const services = [
  {
    slug: 'managed-maintenance-systems',
    title: 'Managed Maintenance Systems',
    short: 'Your equipment’s maintenance, condition, and service history—tracked, managed, and documented in one system.',
    icon: `${cdn}/63d57d07be98890a95c78a1c/66d1385dc8038f9fdbc3840a_Gemini_Generated_Image_d5xeu6d5xeu6d5xe.jpeg`,
    cover: `${cdn}/63d57d07be98890a95c78a1c/66d1391449227acb1ce52e86_66d1390ab136f786b51d8062_Gemini_Generated_Image_vmm1h4vmm1h4vmm1.jpeg`,
    paragraphs: [
      'Our managed maintenance system is designed to take the burden of service tracking off the owner. We monitor maintenance intervals, document equipment condition, and identify developing concerns so routine service and measured repairs can be handled before they become disruptive failures.',
      'Every machine produces useful information. When hour-meter readings, inspection findings, fluid leaks, and oil-analysis trends are collected and evaluated together, they can reveal trouble well before a major failure. That may mean completing an overlooked interval service, replacing a seal while a leak is still minor, or addressing early valvetrain wear before it leads to an engine replacement.',
    ],
  },
  {
    slug: 'on-site-repairs',
    title: 'On-Site Repairs',
    short: 'When equipment goes down, we bring field-ready diagnostics and repair capabilities directly to where it sits.',
    icon: `${cdn}/63d57d07be98890a95c78a1c/63e15c3434ecdaa98c577d69_mechanic_ic.webp`,
    cover: `${cdn}/63d57d07be98890a95c78a1c/63dbfea43c233e6c8d543921_am1.jpg`,
    paragraphs: [
      'From drivability diagnostics to common part failures, our mobile mechanic service is built to get you moving again quickly.',
      'We show up with diagnostic equipment and practical repair options tailored to your vehicle and situation.',
      'Routine maintenance and proactive checks help prevent emergency downtime.',
    ],
  },
  {
    slug: 'mobile-welding',
    title: 'Mobile Welding',
    short: 'From a cracked mower-deck mount to a broken stud on the other side of the creek, our mobile welding capability goes wherever the repair demands.',
    icon: `${cdn}/63d57d07be98890a95c78a1c/63e15c0b8d296ef13dc8e959_welding_icon.webp`,
    cover: `${cdn}/63d57d07be98890a95c78a1c/63dbf139879dea4dd67c27d5_weldingcloge.jpg`,
    paragraphs: [
      'Our mobile welding service handles repair and fabrication without requiring transport to a fixed shop.',
      'We prioritize safe, durable repairs with practical solutions for field conditions and time-sensitive jobs.',
      'Centerfire Services brings the equipment, process discipline, and experience needed for reliable weld outcomes.',
    ],
  },
  {
    slug: 'additional-capabilities',
    title: 'Additional Capabilities',
    short: 'Not every need fits neatly into one category. When it falls outside our core services, we still help you find a practical path forward.',
    icon: `${cdn}/63d57d07be98892211c78a10/63dd4c0fc78b764e379c404a_truck-p-500.webp`,
    cover: `${cdn}/63d57d07be98890a95c78a1c/63dbfebd06f90d325c47c54e_am2.jpg`,
    paragraphs: [
      'Fleet uptime is critical. We support maintenance and repair schedules built around your operations.',
      'Mobile service reduces idle hours and simplifies logistics for teams running multiple vehicles or machines.',
      'We focus on dependable turnaround and clear communication on repair priorities.',
    ],
  },
];

export const faq = [
  {
    q: 'What services does Centerfire Services offer?',
    a: 'Centerfire Services provides mobile mechanic and welding services, including diagnostics, brake repair, battery replacement, fabrication, and scheduled maintenance.',
  },
  {
    q: 'How much does mobile service cost?',
    a: 'Pricing depends on the type of repair, labor time, parts, and travel requirements. Contact us for a clear estimate based on your job.',
  },
  {
    q: 'How do I schedule an appointment?',
    a: 'Call, text, or submit a service request through the website. We will follow up to confirm details and scheduling.',
  },
  {
    q: 'What areas do you serve?',
    a: 'We provide mobile service in Medina, Lakehills, Castroville, Pipe Creek, Mico, Rio Medina, Boerne, and Bandera, Texas.',
  },
  {
    q: 'What should I expect from a mobile visit?',
    a: 'A qualified technician arrives with the tools and diagnostics needed for the job, explains findings clearly, and outlines practical repair options.',
  },
  {
    q: 'Do you also offer welding services?',
    a: 'Yes. In addition to mobile mechanic services, we provide welding and light fabrication support.',
  },
  {
    q: 'How long does a typical job take?',
    a: 'Job length varies by issue and parts availability, but we provide a time estimate before work begins and keep you updated throughout the repair.',
  },
];

export const blogPosts = [
  {
    slug: 'restoring-the-windows-on-an-83-j10-honcho-at-centerfire-services',
    category: 'Welding',
    date: 'May 18, 2022',
    title: 'Restoring the Windows on an 83 J10 Honcho',
    cover: `${cdn}/63d57d07be98890a95c78a1c/63dd5a09862f27e54d502822_319906657_5818627641535803_2053179699888168766_n.jpg`,
    excerpt:
      'A rusted passenger-side window bracket had failed on a classic J10. We rebuilt the support and restored smooth window operation.',
    sections: [
      { type: 'heading', level: 2, text: 'A Classic Truck with a Window Failure' },
      {
        type: 'paragraph',
        text: 'The passenger-side lifting bracket had rusted away, leaving the window unsupported and unable to operate correctly.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd5a68684da5b564bf7ca4_319717304_376567891346579_3178708960385871453_n.jpg`,
        alt: 'Disassembled window system on an 83 J10 door',
      },
      { type: 'heading', level: 2, text: 'Custom Bracket Fabrication' },
      {
        type: 'paragraph',
        text: 'We disassembled the system, retained the reusable components, and fabricated a custom bracket to secure the original leather-padded glass edge.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd5a76976fd3e307104620_319993140_812710636696355_8182688045506668909_n.jpg`,
        alt: 'Custom fabricated window bracket before installation',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd5aa201b9b848ea692fdb_319732055_3376587692555363_2967860472880888567_n.jpg`,
        alt: 'Window bracket installed on the original regulator assembly',
      },
      { type: 'heading', level: 2, text: 'Reliable Final Result' },
      {
        type: 'paragraph',
        text: 'The repair produced a firm, rattle-free hold and restored everyday usability while preserving the truck’s original character.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd5a09862f27e54d502822_319906657_5818627641535803_2053179699888168766_n.jpg`,
        alt: 'Completed window restoration on the 83 J10 Honcho',
      },
    ],
  },
  {
    slug: 'repairing-a-7-3-powerstroke-turbo-on-a-99-f350-truck',
    category: 'Maintenance',
    date: 'September 30, 2022',
    title: 'Repairing a 7.3 Powerstroke Turbo on a 99 F350',
    cover: `${cdn}/63d57d07be98890a95c78a1c/63dd58fc976fd376f81031b9_326528154_1300865950863599_7807143389801485828_n.jpg`,
    excerpt:
      'A broken turbo housing bolt caused an exhaust leak. We extracted the damaged hardware, resealed the assembly, and rebuilt internals.',
    sections: [
      { type: 'heading', level: 2, text: 'Diagnosing the Turbo Leak' },
      {
        type: 'paragraph',
        text: 'On this 7.3 Powerstroke, one top bolt had backed out and another had broken, which opened the housing seal and caused a leak.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd595c684da58d5ebf67e6_326125506_841262730277230_7039469168593006280_n.jpg`,
        alt: 'Turbo assembly showing damaged mounting hardware',
      },
      { type: 'heading', level: 2, text: 'Repair and Rebuild Work' },
      {
        type: 'paragraph',
        text: 'We extracted the broken bolt, restored proper clamping force, and rebuilt the turbo internals to improve long-term reliability.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd596dac6c240f5cf04bf3_326193881_866255264709569_8102215013020871954_n.jpg`,
        alt: 'Turbo disassembled for internal rebuild',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd597a01b9b8659d6921d6_326128636_612167094053712_3741322602547454395_n.jpg`,
        alt: 'Reassembled turbo after repair and rebuild',
      },
      { type: 'heading', level: 3, text: 'Back on the Road' },
      {
        type: 'paragraph',
        text: 'With the leak eliminated and internals refreshed, the truck returned to service with stronger performance and dependable operation.',
      },
    ],
  },
  {
    slug: 'repairs-on-a-mahindra-4540-tractor-at-centerfire-services',
    category: 'Maintenance',
    date: 'September 30, 2022',
    title: 'Mahindra 4540 Tractor Driveline Repair',
    cover: `${cdn}/63d57d07be98890a95c78a1c/63dd57c6d68662133aca03e0_326104078_1715614542196216_8279993383976777948_n.jpg`,
    excerpt:
      'A loose carrier bearing and missing hardware on a Mahindra 4540 caused driveline instability. We completed a full corrective repair.',
    sections: [
      { type: 'heading', level: 2, text: 'Carrier Bearing Failure' },
      {
        type: 'paragraph',
        text: 'The front driveshaft carrier bearing had come loose and impacted surrounding components, leading to serious driveline issues.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd58546338100e913bedd0_326388094_1356586448422616_143955441818865304_n.jpg`,
        alt: 'Mahindra 4540 component damage near the carrier bearing',
      },
      { type: 'heading', level: 2, text: 'Hardware and Mount Correction' },
      {
        type: 'paragraph',
        text: 'We corrected mounting alignment, replaced missing fasteners, and secured the assembly to restore proper operation.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd585d2a62dfcf0f3d50dc_326199155_908079600605010_5504974207443259060_n.jpg`,
        alt: 'Completed driveline repair on Mahindra 4540 tractor',
      },
      { type: 'heading', level: 2, text: 'Operational Result' },
      {
        type: 'paragraph',
        text: 'The tractor returned to reliable service, and the customer was able to resume normal work with confidence.',
      },
    ],
  },
  {
    slug: 'how-to-handle-aging-and-corroded-battery-terminals',
    category: 'Maintenance',
    date: 'September 30, 2022',
    title: 'Repairing Aging and Corroded Battery Terminals',
    cover: `${cdn}/63d57d07be98890a95c78a1c/63dd54464df23b3b7e0d61ce_seal.jpg`,
    excerpt:
      'Corroded terminal ends can cause hard-start and continuity issues. This process restores durable, sealed battery connections.',
    sections: [
      { type: 'heading', level: 2, text: 'The Problem with Corroded Connections' },
      {
        type: 'paragraph',
        text: 'Extended exposure can corrode cable ends and degrade electrical continuity, leading to intermittent performance.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd545eedcda151ff421339_croded.jpg`,
        alt: 'Corroded battery terminal and cable ends before repair',
      },
      { type: 'heading', level: 2, text: 'Connection Rebuild Process' },
      {
        type: 'paragraph',
        text: 'We removed contaminated wire ends, crimped and soldered individual eyelets, then sealed each joint with heat shrink for protection.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd54807e62bedf79bf0d2e_clean.jpg`,
        alt: 'Battery cables after cleaning and eyelet repair',
      },
      { type: 'heading', level: 2, text: 'Final Protection and Fitment' },
      {
        type: 'paragraph',
        text: 'The finished setup was secured to solid lead terminal clamps and treated with battery protector to reduce future corrosion.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd54464df23b3b7e0d61ce_seal.jpg`,
        alt: 'Sealed battery terminal assembly after final treatment',
      },
    ],
  },
  {
    slug: 'quick-and-effective-repair-of-leaky-hydraulic-cylinders-at-centerfire-services',
    category: 'Maintenance',
    date: 'May 18, 2022',
    title: 'Field Repair of Leaky Hydraulic Cylinders',
    cover: `${cdn}/63d57d07be98890a95c78a1c/63dd5b5517fdf72acff108c3_319995409_1204251680162365_3491112727158569458_n.jpg`,
    excerpt:
      'A Case 580 Super M had leaking hydraulic cylinders. We completed a mobile field repair to minimize downtime and restore function.',
    sections: [
      { type: 'heading', level: 2, text: 'High-Impact Hydraulic Leak' },
      {
        type: 'paragraph',
        text: 'The customer needed two leaking cylinders repaired quickly to keep a Case 580 Super M backhoe in service.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd5bb67efa41485d27daa9_319940211_667712234769939_1966738116621755061_n.jpg`,
        alt: 'Leaking hydraulic cylinder on Case 580 Super M before repair',
      },
      { type: 'heading', level: 2, text: 'On-Site Service Execution' },
      {
        type: 'paragraph',
        text: 'Our team removed and serviced the cylinders in the field, reducing transport delays and returning the machine faster.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd5bc1bb10481f39f5178b_319874858_1506710326504355_9164801168619895461_n.jpg`,
        alt: 'Completed hydraulic cylinder repair work on-site',
      },
      { type: 'heading', level: 2, text: 'Restored Productivity' },
      {
        type: 'paragraph',
        text: 'The backhoe returned to full operation with minimal downtime, which let the customer resume scheduled work quickly.',
      },
    ],
  },
  {
    slug: 'durable-swaybar-bushings-and-mounts-for-a-lifetime-of-performance',
    category: 'Welding',
    date: 'April 1, 2022',
    title: 'Durable Swaybar Bushings and Mount Fabrication',
    cover: `${cdn}/63d57d07be98890a95c78a1c/63dd5c3586b7f142a0006bb8_319853266_556515966293716_2437594003346508720_n.jpg`,
    excerpt:
      'We fabricated heavy-duty swaybar bushings and mounts to deliver long service life and reliable suspension support.',
    sections: [
      { type: 'heading', level: 2, text: 'Long-Term Durability Request' },
      {
        type: 'paragraph',
        text: 'The customer needed swaybar bushings and mounts designed for extended life under demanding use.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd5c929b51ae295eb6fd4f_319753811_1209076319714552_2653527956958471731_n.jpg`,
        alt: 'Swaybar mounting components during fabrication',
      },
      { type: 'heading', level: 2, text: 'Fabrication and Fitment' },
      {
        type: 'paragraph',
        text: 'We fabricated and fitted custom components to improve strength, alignment, and long-term reliability.',
      },
      {
        type: 'image',
        src: `${cdn}/63d57d07be98890a95c78a1c/63dd5ca43b9fd8571c149ea5_319790093_3003636909929622_1491293430826480165_n.jpg`,
        alt: 'Final installed swaybar bushings and mounts',
      },
      { type: 'heading', level: 2, text: 'Outcome' },
      {
        type: 'paragraph',
        text: 'The result was a clean, durable installation that delivered the confidence and performance the customer expected.',
      },
    ],
  },
];

export const serviceHours = [
  { day: 'Monday - Friday', hours: '7 am - 9 pm' },
  { day: 'Saturday', hours: '7 am - 7 pm' },
  { day: 'Sunday', hours: 'Closed' },
];
